// ───────────────────────────────────────────────────────────────
// Gabarit commun des pages légales (mentions légales, confidentialité, CGU)
// Mêmes codes visuels que la landing : en-tête avec logo, pied de page avec
// liens légaux, Manrope (titres) / Inter (texte), palette .pp-landing.
// Fond sombre propre à la page : lisible quel que soit le thème de
// l'application (le thème clair par défaut rendait les titres invisibles).
// Contrastes sur #0F1117 : titres 18:1, texte 12,8:1, secondaire 7,4:1,
// liens 9,3:1 (WCAG AA).
// ───────────────────────────────────────────────────────────────
import { useEffect } from "react";
import { PUBLISHER_LABEL, COPYRIGHT_YEAR } from "../config/legal";
import "../styles/design-tokens.css"; // polices Manrope / Inter

const logoIcon = "/assets/logo-icon-execpro360.png";

const LEGAL_LINKS = [
  { key: "mentions", href: "/mentions-legales", label: "Mentions légales" },
  { key: "confidentialite", href: "/confidentialite", label: "Confidentialité" },
  { key: "cgu", href: "/cgu", label: "CGU" },
];

// Scopé sous .legal-page (et non :root) : le thème de l'application pose ses
// propres variables en style inline sur <html> — même pattern que .pp-landing.
const CSS = `
.legal-page {
  --ink: #0F1117;
  --hairline: #2D3348;
  --text: #F9FAFB;
  --text-body: #D1D5DB;
  --text-muted: #9CA3AF;
  --brand: #22D15B;
  background: var(--ink); color: var(--text-body); min-height: 100vh;
  display: flex; flex-direction: column;
  font-family: 'Inter', system-ui, sans-serif;
}
.legal-page a:focus-visible { outline: 2px solid var(--brand); outline-offset: 2px; border-radius: 2px; }

.legal-header { position: sticky; top: 0; z-index: 20; background: #0F1117ee; backdrop-filter: blur(10px); border-bottom: 1px solid var(--hairline); }
.legal-header-inner { max-width: 1180px; margin: 0 auto; padding: 0 40px; height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.legal-brand { display: flex; align-items: center; gap: 10px; text-decoration: none; }
.legal-brand img { height: 30px; width: auto; display: block; }
.legal-brand span { font-family: 'Manrope', system-ui, sans-serif; font-size: 17px; font-weight: 800; color: var(--text); letter-spacing: -0.01em; }
.legal-brand .legal-brand-accent { color: var(--brand); }
.legal-back { font-family: 'Manrope', system-ui, sans-serif; font-size: 14px; font-weight: 600; color: var(--text-muted); text-decoration: none; white-space: nowrap; }
.legal-back:hover { color: var(--text); }

.legal-tabs { max-width: 760px; width: 100%; margin: 0 auto; padding: 24px 24px 0; box-sizing: border-box; display: flex; flex-wrap: wrap; gap: 6px 22px; border-bottom: 1px solid var(--hairline); }
.legal-tabs a { font-family: 'Manrope', system-ui, sans-serif; font-size: 13px; font-weight: 600; color: var(--text-muted); text-decoration: none; padding-bottom: 10px; border-bottom: 2px solid transparent; margin-bottom: -1px; }
.legal-tabs a:hover { color: var(--text); }
.legal-tabs a[aria-current="page"] { color: var(--text); border-bottom-color: var(--brand); }

.legal-main { flex: 1; max-width: 760px; width: 100%; margin: 0 auto; padding: 40px 24px 64px; box-sizing: border-box; overflow-wrap: break-word; }
.legal-main h1 { font-family: 'Manrope', system-ui, sans-serif; font-size: 32px; font-weight: 800; line-height: 1.2; letter-spacing: -0.01em; color: var(--text); margin: 0 0 8px; }
.legal-main .legal-updated { font-size: 13px; color: var(--text-muted); margin: 0 0 32px; }
.legal-main h2 { font-family: 'Manrope', system-ui, sans-serif; font-size: 18px; font-weight: 700; line-height: 1.35; color: var(--text); margin: 36px 0 10px; padding-top: 24px; border-top: 1px solid var(--hairline); }
.legal-main p, .legal-main ul { font-size: 15px; line-height: 1.75; color: var(--text-body); margin: 0 0 12px; }
.legal-main ul { padding-left: 22px; }
.legal-main li { margin-bottom: 6px; }
.legal-main strong { color: var(--text); font-weight: 600; }
.legal-main a { color: var(--brand); text-decoration: underline; text-underline-offset: 3px; }

.legal-footer { border-top: 1px solid var(--hairline); padding: 28px 40px; }
.legal-footer-inner { max-width: 1180px; margin: 0 auto; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px; font-size: 12px; color: var(--text-muted); }
.legal-footer-brand { display: flex; align-items: center; gap: 8px; }
.legal-footer-brand img { height: 20px; }
.legal-footer-brand span { font-family: 'Manrope', system-ui, sans-serif; font-size: 13px; font-weight: 700; color: var(--text); }
.legal-footer-links { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.legal-footer-links a { color: var(--text-muted); text-decoration: none; border-bottom: 1px solid var(--hairline); }
.legal-footer-links a:hover { color: var(--text); }

@media (max-width: 640px) {
  .legal-header-inner { padding: 0 16px; }
  .legal-tabs { padding: 20px 16px 0; }
  .legal-main { padding: 28px 16px 48px; }
  .legal-main h1 { font-size: 26px; }
  .legal-footer { padding: 24px 16px; }
}
`;

export default function LegalLayout({ current, title, updated, children }) {
  useEffect(() => {
    const previous = document.title;
    document.title = `${title} — ExecPro360`;
    return () => { document.title = previous; };
  }, [title]);

  return (
    <div className="legal-page">
      <style>{CSS}</style>

      <header className="legal-header">
        <div className="legal-header-inner">
          <a href="/" className="legal-brand" aria-label="ExecPro360 — accueil">
            <img src={logoIcon} alt="" />
            <span>ExecPro<span className="legal-brand-accent">360</span></span>
          </a>
          <a href="/" className="legal-back">← Retour à l'accueil</a>
        </div>
      </header>

      <nav className="legal-tabs" aria-label="Pages légales">
        {LEGAL_LINKS.map(l => (
          <a key={l.key} href={l.href} aria-current={l.key === current ? "page" : undefined}>{l.label}</a>
        ))}
      </nav>

      <main className="legal-main">
        <h1>{title}</h1>
        <p className="legal-updated">{updated}</p>
        {children}
      </main>

      <footer className="legal-footer">
        <div className="legal-footer-inner">
          <div className="legal-footer-brand">
            <img src={logoIcon} alt="" />
            <span>ExecPro360</span>
          </div>
          <div>© {COPYRIGHT_YEAR} ExecPro360 · {PUBLISHER_LABEL} · Hébergé en UE</div>
          <nav className="legal-footer-links" aria-label="Liens légaux">
            {LEGAL_LINKS.map(l => <a key={l.key} href={l.href}>{l.label}</a>)}
          </nav>
        </div>
      </footer>
    </div>
  );
}
